import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { Button, Icon, Intent, Spinner } from "@blueprintjs/core";
import { observable, action } from 'mobx';
import Toast from "helpers/toaster";
import { Api } from 'services/Api';
import debounce from 'services/debounce';
import FormDrawer from 'components/FormDrawer';
import FormSubmissionModal from './FormSubmissionModal';

const PER_PAGE = 20;

// Add a form (checklist, questionnaire) to a work order, in the same panel
// style as Add material / Add labor (styles: line-item-picker.scss, pick-):
// a search, then the forms with how many are already on this work order and
// a round add button. Adding one opens it right away so it can be filled in.
// It searches on its own, so the Forms page's search is left alone.
@inject("viewStore", "workReportStore", "formSubmissionStore")
@observer
class AddFormModal extends Component {
  @observable showFormSubmissionModal = false;
  @observable attachingId = null; // one at a time; stops double attaches
  @observable term = '';
  @observable results = { isLoading: false, isError: false, objects: [], meta: null };
  @observable active = -1;
  request = 0;

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.term = '';
      this.active = -1;
      this.runSearch(1);
    }
  }

  componentDidMount() {
    if (this.props.isOpen) this.runSearch(1);
  }

  @action
  runSearch = (page = 1) => {
    const id = ++this.request;
    this.results = { ...this.results, isLoading: true, isError: false };
    Api.post('/forms/search', { search: { term: this.term.trim() || '*', per_page: PER_PAGE, page, order: { name: 'asc' } } })
      .then(action((response) => {
        if (id !== this.request) return;
        this.results = { isLoading: false, isError: false, objects: response.data.data || [], meta: response.data.meta || null };
        this.active = -1;
      }))
      .catch(action(() => {
        if (id !== this.request) return;
        this.results = { isLoading: false, isError: true, objects: [], meta: null };
      }));
  };

  searchSoon = debounce(() => this.runSearch(1), 300);

  onSearchChange = (event) => {
    this.term = event.target.value;
    this.searchSoon();
  };

  onSearchKeyDown = (event) => {
    const { objects } = this.results;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.active = Math.min(this.active + 1, objects.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.active = Math.max(this.active - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.active >= 0 && objects[this.active]) this.attach(objects[this.active]);
      else this.runSearch(1);
    }
  };

  // How many of each form are already on this work order (by name).
  get onRecord() {
    const record = this.props.workReportStore.workReport.object;
    const counts = {};
    ((record && record.attributes.forms) || []).forEach((form) => {
      counts[form.name] = (counts[form.name] || 0) + 1;
    });
    return counts;
  }

  attach(form) {
    if (this.attachingId) return;
    const { attributes } = this.props.workReportStore.workReport.object;
    form.attributes.work_report_id = attributes.id;
    attributes.forms.push(form.attributes);
    this.attachingId = form.attributes.id;
    this.props.workReportStore.updateWorkReportForm(attributes)
      .then((response) => {
        this.attachingId = null;
        // The list held the form itself until now; show what was saved (who
        // added it and when) in its place.
        const saved = response && response.data && response.data.data && response.data.data.attributes;
        const index = attributes.forms.lastIndexOf(form.attributes);
        if (saved && index >= 0) attributes.forms.splice(index, 1, { ...form.attributes, ...saved, id: saved.id || response.data.data.id });
        this.props.onClose();
        this.props.formSubmissionStore.getFormSubmission(response.data.data);
        this.showFormSubmissionModal = true;
        Toast.show({ message: `${form.attributes.name} added. Fill it in now, or later from the work order.`, intent: Intent.SUCCESS });
      })
      .catch((error) => {
        this.attachingId = null;
        // It didn't attach: take it back off the list.
        const index = attributes.forms.lastIndexOf(form.attributes);
        if (index >= 0) attributes.forms.splice(index, 1);
        const data = error && error.response && error.response.data;
        const reason = data && (data.errors || data.error);
        Toast.show({ message: reason ? [].concat(reason).join(' ') : "Couldn't add the form. Try again.", intent: Intent.DANGER });
      });
  }

  renderRow(form, index) {
    const name = form.attributes.name;
    const count = this.onRecord[name] || 0;
    const attaching = this.attachingId === form.attributes.id;
    const blocked = Boolean(this.attachingId) && !attaching;
    return (
      <li
        key={form.id}
        id={`form-option-${index}`}
        role="option"
        aria-selected={this.active === index}
        className={`pick-row pick-row--form${this.active === index ? ' is-active' : ''}`}
        onMouseEnter={() => { this.active = index; }}
        onClick={() => { if (!blocked) this.attach(form); }}
      >
        <span className="pick-form__icon" aria-hidden="true"><Icon icon="form" iconSize={16} /></span>
        <div className="pick-row__main">
          <span className="pick-row__name">{name}</span>
          {count > 0 && <span className="pick-row__count">{count} on this work order</span>}
        </div>
        <button
          type="button"
          className="pick-add"
          aria-label={`Add ${name} to this work order`}
          aria-disabled={blocked ? true : undefined}
          tabIndex={-1}
          onClick={(event) => { event.stopPropagation(); if (!blocked) this.attach(form); }}
        >
          {attaching ? <Spinner size={14} /> : <Icon icon="plus" iconSize={14} />}
        </button>
      </li>
    );
  }

  renderResults() {
    const { objects, isLoading, isError, meta } = this.results;
    const term = this.term.trim();
    if (isLoading && objects.length === 0) {
      return (
        <ul className="pick-list" aria-busy="true" aria-label="Loading">
          {[0, 1, 2, 3].map((index) => (
            <li key={index} className="pick-row pick-row--bone" aria-hidden="true">
              <div className="pick-row__main"><span className="dl-bone" style={{ width: 200, height: 14 }} /></div>
            </li>
          ))}
        </ul>
      );
    }
    if (isError) {
      return (
        <div className="pick-empty" role="alert">
          <p className="pick-empty__title">The forms couldn't be loaded.</p>
          <p className="pick-empty__hint">Check your connection, then try again.</p>
          <div className="pick-empty__actions"><Button icon="refresh" text="Try again" onClick={() => this.runSearch(1)} /></div>
        </div>
      );
    }
    if (objects.length === 0) {
      return (
        <div className="pick-empty" role="status">
          {term ? (
            <>
              <p className="pick-empty__title">No forms match “{term}”.</p>
              <p className="pick-empty__hint">Check the spelling, or build the form under Forms.</p>
            </>
          ) : (
            <>
              <p className="pick-empty__title">No forms yet.</p>
              <p className="pick-empty__hint">Build one under Forms (a furnace inspection checklist, say), then add it here.</p>
            </>
          )}
        </div>
      );
    }
    const page = (meta && meta.page) || 1;
    const pages = (meta && meta.total_pages) || 1;
    return (
      <>
        <ul className="pick-list" role="listbox" id="form-results" aria-label="Forms">
          {objects.map((form, index) => this.renderRow(form, index))}
        </ul>
        {pages > 1 && (
          <nav className="pick-pages" aria-label="Pages">
            <span className="pick-pages__info">Page {page} of {pages}{meta && meta.total_records ? `, ${meta.total_records} forms` : ''}</span>
            <Button minimal icon="chevron-left" text="Previous" disabled={page <= 1} onClick={() => this.runSearch(page - 1)} />
            <Button minimal rightIcon="chevron-right" text="Next" disabled={page >= pages} onClick={() => this.runSearch(page + 1)} />
          </nav>
        )}
      </>
    );
  }

  render() {
    const { isOpen, onClose, formSubmission } = this.props;
    const activeId = this.active >= 0 ? `form-option-${this.active}` : undefined;
    return (
      <React.Fragment>
        <FormDrawer
          title="Add a form to this work order"
          icon="form"
          tone="field"
          size="min(640px, 100vw)"
          className="pick"
          isOpen={isOpen}
          onClose={onClose}
          busy={Boolean(this.attachingId)}
          footer={(
            <div className="pick-tally">
              <span className="pick-tally__hint">Adding a form opens it, so the tech can start filling it in.</span>
              <div className="pick-tally__actions">
                <Button text="Close" onClick={onClose} disabled={Boolean(this.attachingId)} />
              </div>
            </div>
          )}
        >
          <div className="pick-search">
            <label className="sr-only" htmlFor="form-search-input">Find a form</label>
            <div className="pick-search__field">
              <Icon icon="search" iconSize={16} className="pick-search__icon" />
              <input
                id="form-search-input"
                type="search"
                autoComplete="off"
                autoFocus
                role="combobox"
                aria-expanded="true"
                aria-controls="form-results"
                aria-activedescendant={activeId}
                placeholder="Search forms, e.g. Furnace inspection"
                value={this.term}
                onChange={this.onSearchChange}
                onKeyDown={this.onSearchKeyDown}
              />
              {this.term && (
                <button type="button" className="pick-search__clear" aria-label="Clear search" onClick={() => { this.term = ''; this.runSearch(1); }}>
                  <Icon icon="cross" iconSize={14} />
                </button>
              )}
            </div>
          </div>
          <p className="pick-help"><kbd>↓</kbd> <kbd>↑</kbd> to pick, <kbd>Enter</kbd> to add.</p>
          {this.renderResults()}
        </FormDrawer>
        <FormSubmissionModal
          isOpen={this.showFormSubmissionModal}
          formSubmission={formSubmission}
          onClose={() => { this.showFormSubmissionModal = false; }}
        />
      </React.Fragment>
    );
  }
}

export default AddFormModal;
